/**
 * Clinafy Logo Component
 *
 * Uses the logo.webp image file.
 * Supports light/dark variants and multiple sizes.
 */

import React from 'react';
import { Box } from '@mui/material';

// ============================================================================
// Types
// ============================================================================

interface LogoProps {
  variant?: 'light' | 'dark' | 'colored';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  onClick?: () => void;
}

// ============================================================================
// Size Configurations
// ============================================================================

const sizeConfig = {
  sm: {
    height: 36,
  },
  md: {
    height: 44,
  },
  lg: {
    height: 56,
  },
  xl: {
    height: 80,
  },
  xxl: {
    height: 110,
  },
};

// ============================================================================
// Main Logo Component
// ============================================================================

const Logo: React.FC<LogoProps> = ({
  variant = 'colored',
  size = 'md',
  onClick,
}) => {
  const config = sizeConfig[size];

  // For light variant on dark backgrounds, we can apply a filter
  const filter = variant === 'light' ? 'brightness(0) invert(1)' : 'none';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
        textDecoration: 'none',
        transition: 'opacity 0.2s ease',
        '&:hover': onClick ? {
          opacity: 0.85,
        } : {},
      }}
    >
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/clinafy-logo.png`}
        alt="Clinafy"
        sx={{
          height: config.height,
          width: 'auto',
          objectFit: 'contain',
          filter: filter,
          transition: 'filter 0.3s ease',
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          // Fallback to old logo if new one fails
          e.currentTarget.src = `${process.env.PUBLIC_URL}/logo.webp`;
        }}
      />
    </Box>
  );
};

export default Logo;
